import { FunctionComponent, useCallback, useEffect, useState } from "react"

import { useNavigate } from "react-router-dom"
import {
  useGetUserAssignmentsQuery,
  useGetOrganizationSimulationsQuery,
  useAssignSimulationToOrganizationMutation,
} from "../app/services/meseeknaApiV2"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import SimulationCard from "../components/dashboard-simulation-card"
import Sidebar from "../features/sidebar/components/sidebar"
import { Simulation } from "../features/simulation/types"
import * as utils from "../features/simulation/utils"
import { setTestTitle } from "../features/auth/authSlice"
import {
  useLazyGetMeasureScoresByUserIdQuery,
  useLazyGetMicroLearningByUserIdQuery,
  usePostUserapiAdminTestsGetRunIdMutation,
} from "../app/services/meseeknaApi"

type DashboardProps = {
  simulations?: Simulation[]
}

const SimulationDasboard: FunctionComponent<DashboardProps> = ({
  simulations: initialSimulations = [],
}) => {
  const [simulations, setSimulations] = useState<Simulation[]>([])
  const navigate = useNavigate()
  const dispatch = useTypedDispatch()
  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const userId = authStore?.user?.id
  const orgId = Number(authStore?.organization?.id)
  const [simCompleted, setSimCompleted] = useState<Record<number, boolean>>({})
  const [simState, setSimState] = useState<Record<number, string>>({})
  const [getRunId] = usePostUserapiAdminTestsGetRunIdMutation()

  // New hooks for assignments and org tests
  const {
    data: assignmentsData,
    isLoading: isAssignmentsLoading,
    refetch: refetchAssignments,
  } = useGetUserAssignmentsQuery(userId, { skip: !userId })
  const { data: orgTestsData, isLoading: isOrgTestsLoading } =
    useGetOrganizationSimulationsQuery(orgId, { skip: !orgId })
  const [assignSimulationToOrganization] =
    useAssignSimulationToOrganizationMutation()

  const [triggerMicroLearningFetch] = useLazyGetMicroLearningByUserIdQuery()
  const [triggerMeasureScoresFetch] = useLazyGetMeasureScoresByUserIdQuery()

  const getRunIdResponse = async (
    testId: number,
    orgId: number,
    userId: number,
  ) => {
    const response = getRunId({
      testId,
      organizationId: orgId,
      userId: userId,
    }).unwrap()
    return response
  }

  useEffect(() => {
    if (!userId || !orgId) return
    if (isAssignmentsLoading || isOrgTestsLoading) return
    if (!assignmentsData) return

    const processAssignments = async () => {
      try {
        // If user has assignments, display them
        if (assignmentsData.data && assignmentsData.data.length > 0) {
          // Map assignments to include test details for display
          const testMap = new Map<
            number,
            {
              testId: number
              simulation: { title: string; shortName: string; imgUrl?: string; imageData?: string }
            }
          >()
          if (orgTestsData && orgTestsData.data) {
            orgTestsData.data.forEach(
              (sim: {
                testId: number
                simulation: {
                  title: string
                  shortName: string
                  imgUrl?: string
                  imageData?: string
                }
              }) => testMap.set(sim.testId, sim),
            )
          }
          const sims = assignmentsData.data.map(
            (assignment: {
              testId: number
              assignmentId?: number
              runId?: string
            }) => {
              const sim = testMap.get(assignment.testId)
              return {
                testId: assignment.testId,
                runId:
                  assignment.assignmentId ??
                  (assignment.runId ? Number(assignment.runId) : undefined),
                title:
                  sim?.simulation?.title || `Simulation ${assignment.testId}`,
                description: "",
                imgUrl: sim?.simulation?.imgUrl,
                imageData: sim?.simulation?.imageData,
                shortName: sim?.simulation?.shortName,
              }
            },
          )
          setSimulations(sims)
          // Check assignment status for each sim using getRunId
          sims.forEach(async (sim) => {
            try {
              const response = await getRunIdResponse(sim.testId, orgId, userId)
              // Check for dateCompleted in the response (array or object)
              let dateCompleted: string | undefined
              if (Array.isArray(response.data) && response.data.length > 0) {
                dateCompleted = response.data[0].dateCompleted
              } else if (
                response.data &&
                typeof response.data === "object" &&
                "dateCompleted" in response.data
              ) {
                dateCompleted = (response.data as { dateCompleted?: string })
                  .dateCompleted
              } else {
                dateCompleted = undefined
              }
              console.log(dateCompleted, "dateCompleted")
              if (dateCompleted) {
                // Get user role - microlearning is only for TeamMember (role 4)
                const userRole = authStore?.user?.roles?.[0]?.roleId || authStore?.user?.role
                const isTeamMember = userRole === 4

                // For candidates (role 5), just show "Completed" - no microlearning
                if (!isTeamMember) {
                  setSimState((prev) => ({
                    ...prev,
                    [sim.testId]: "Completed",
                  }))
                } else {
                  // TeamMember role - check microlearning status
                  let state = "Reveal superpower"
                  const { data } = await triggerMeasureScoresFetch({ id: userId })
                  // check if the microlearning exists for this test
                  if (data && data.data.length > 0) {
                    const isMicroLearning = data.data.some(
                      (score) => score.isMicroLearning == 1,
                    )
                    console.log(isMicroLearning, "isMicroLearning")
                    if (!isMicroLearning) {
                      state = "Test completed & No microlearnings"
                    } else {
                      const { data } = await triggerMicroLearningFetch({
                        userId: userId,
                        assignedTestId: Number(sim.runId),
                      })
                      if (data && data.data.length > 0) {
                        const assignedMicroLearning = data.data.find(
                          (ml) => ml.assignedTestId === sim.runId,
                        )
                        console.log(
                          assignedMicroLearning,
                          "assignedMicroLearning",
                        )
                        if (assignedMicroLearning) {
                          state = "Training in-progress"
                          const allWeeksAnswered =
                            assignedMicroLearning.months.length > 0 &&
                            assignedMicroLearning.months.every(
                              (month) =>
                                month.weeks.length > 0 &&
                                month.weeks.every(
                                  (week) => week.response !== null,
                                ),
                            )
                          console.log(allWeeksAnswered, "allWeeksAnswered")
                          if (allWeeksAnswered) {
                            state = "Completed test and training"
                          }
                        }
                      } else {
                        state = "Microlearning selection pending"
                      }
                    }
                  }

                  setSimState((prev) => ({
                    ...prev,
                    [sim.testId]: state,
                  }))
                }
              }
              setSimCompleted((prev) => ({
                ...prev,
                [sim.testId]: !!dateCompleted,
              }))
            } catch (e) {
              // If error, assume not completed
              setSimCompleted((prev) => ({
                ...prev,
                [sim.testId]: false,
              }))
            }
          })
        } else if (
          orgTestsData &&
          orgTestsData.data &&
          orgTestsData.data.length > 0
        ) {
          // No assignments: assign the user to the first available org test
          const testToAssign = orgTestsData.data[0]
          await assignSimulationToOrganization({
            orgId,
            testId: testToAssign.testId,
            body: { userId },
          })
          // Refetch assignments to show the new one
          refetchAssignments()
        } else {
          setSimulations([])
        }
      } catch (e) {
        console.error(e)
      }
    }
    processAssignments()
  }, [
    userId,
    orgId,
    assignmentsData,
    orgTestsData,
    isAssignmentsLoading,
    isOrgTestsLoading,
  ])

  const onCardContainerClick = useCallback(
    (testId: number, runId: number, title: string, state: string) => {
      dispatch(setTestTitle(title))
      const userRole = authStore?.user?.roles?.[0]?.roleId || authStore?.user?.role
      const isTeamMember = userRole === 4
      const isCandidate = userRole === 5

      if (state == undefined) {
        // Simulation not completed - go to tutorial
        navigate(`/simulations/${testId}/${runId}/tutorial/intro`)
      } else if (isTeamMember) {
        // TeamMember with completed state - go to complete page (with microlearning)
        navigate(`/simulations/complete/${testId}/${runId}`)
      } else if (isCandidate && state === "Completed") {
        // Candidate with completed state - go to complete page (no microlearning)
        navigate(`/simulations/complete/${testId}/${runId}`)
      } else {
        // Other cases - go to tutorial
        navigate(`/simulations/${testId}/${runId}/tutorial/intro`)
      }
    },
    [navigate, authStore],
  )

  useEffect(() => {
    if (simulations.length > 0) {
      return
    }
    if (initialSimulations.length > 0) {
      console.log("Setting initial simulations")
      setSimulations(initialSimulations)
    }
  }, [initialSimulations, simulations.length])

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col overflow-hidden">
        <div
          id="simulationContainer"
          className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200 overflow-auto">
          <div className="w-full h-full flex-1 flex flex-col items-start justify-start py-0 px-0 box-border gap-[12px] text-base text-gray-700">
            <div
              id="contentContainer"
              className="self-stretch h-full flex-1 flex flex-row items-start gap-[19px] text-sm text-gray-500">
              <div className="w-full flex flex-col items-start justify-start gap-[24px]">
                {/* Page Title */}
                <div className="w-full">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Your Simulation
                  </h1>
                  <p className="text-gray-500">
                    Click the card to begin your cognitive assessment experience.
                  </p>
                </div>

                {/* Side-by-side layout: Simulation Card + Info Panel */}
                <div className="w-full flex flex-col lg:flex-row gap-8 lg:items-stretch items-start">
                  {/* Left: Simulation Card */}
                  <div className="flex-shrink-0 flex">
                    {isAssignmentsLoading || isOrgTestsLoading ? (
                      <div className="w-[280px] h-[320px] bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-8 w-8 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                        <div className="text-gray-500 text-sm">Loading...</div>
                      </div>
                    ) : simulations.length > 0 ? (
                      <div className="flex flex-row flex-wrap gap-6">
                        {simulations.map((simulation, index) => {
                          const completed = simCompleted[Number(simulation.testId)]
                          const state = simState[Number(simulation.testId)]
                          return (
                            <div key={index} style={{ position: "relative" }}>
                              <SimulationCard
                                id={simulation.testId || -99}
                                name={simulation.title ?? "Simulation"}
                                description={simulation.description ?? ""}
                                imageData={simulation.imageData}
                                imgUrl={
                                  utils.getLocalSimLogo(
                                    simulation.shortName ?? "",
                                  ) || simulation?.imgUrl || undefined
                                }
                                clickCallback={() =>
                                  onCardContainerClick(
                                    simulation.testId ?? -99,
                                    simulation.runId ?? -99,
                                    simulation.title ?? "Simulation",
                                    state,
                                  )
                                }
                                state={state}
                                completed={!!completed}
                              />
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="w-[280px] h-[200px] bg-white rounded-xl border border-gray-200 flex items-center justify-center">
                        <div className="text-gray-500 font-medium">Ready to predict performance? Deploy your first simulation to unlock cognitive insights.</div>
                      </div>
                    )}
                  </div>

                  {/* Right: Info Panel */}
                  <div className="flex-1 max-w-xl bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Before You Begin</h3>

                    {/* Quick Facts Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        30 Minutes
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Chrome + Laptop
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Come As You Are
                      </span>
                    </div>

                    {/* Brief intro - always visible */}
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                      There are no right or wrong answers—we&apos;re mapping your unique cognitive patterns to reveal how you naturally think and solve problems.
                    </p>

                    {/* Expandable Details Section */}
                    <details className="group">
                      <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-700 mb-3">
                        <span>Learn more about your simulation</span>
                        <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>

                      <div className="space-y-4 pt-2 border-t border-gray-100">
                        {/* Science Blurb */}
                        <div className="bg-indigo-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-indigo-900 mb-2">Discover Your Cognitive Blueprint</h4>
                          <p className="text-xs text-indigo-800 leading-relaxed">
                            Meseekna uses immersive simulations backed by 500+ peer-reviewed studies to reveal how you naturally think, solve problems, and collaborate. Unlike personality tests that measure what you say, we capture how you actually make decisions in realistic scenarios.
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className="text-xs px-2 py-1 bg-white text-indigo-700 rounded-full font-medium">500+ Peer-Reviewed Studies</span>
                            <span className="text-xs px-2 py-1 bg-white text-indigo-700 rounded-full font-medium">Personalized Insights</span>
                            <span className="text-xs px-2 py-1 bg-white text-indigo-700 rounded-full font-medium">Precision Development</span>
                          </div>
                        </div>

                        {/* What to Expect */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">What to Expect</h4>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                              <svg className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <div>
                                <p className="text-xs font-medium text-gray-900">Realistic Challenge</p>
                                <p className="text-xs text-gray-500">You may encounter moments of uncertainty or complexity. This is intentional—it mirrors real-world conditions and makes the simulation predictive of actual performance.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                              <svg className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div>
                                <p className="text-xs font-medium text-gray-900">Not a Test</p>
                                <p className="text-xs text-gray-500">Unlike personality tests, we capture how you actually think, not what you say you&apos;d do. Focus on your natural responses.</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Tips */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">Tips for Best Results</h4>
                          <ul className="text-xs text-gray-600 space-y-1.5">
                            <li className="flex items-start gap-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full mt-1.5"></span>
                              A focused experience validated by decades of cognitive science research. Complete in one sitting for best results.
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full mt-1.5"></span>
                              Use Chrome browser on a laptop for accurate measurement. Disable ad blockers. Mobile devices won&apos;t work.
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full mt-1.5"></span>
                              No preparation needed—we measure how you naturally think. Tutorial included to get you started.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </details>

                    {/* Confidentiality - always visible, pushed to bottom */}
                    <div className="mt-auto pt-4 bg-orange-50 border-l-4 border-orange-400 p-2.5 rounded-r text-xs text-orange-700">
                      <strong>Keep It Confidential:</strong> Don&apos;t share scenario details with teammates who haven&apos;t completed it yet.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimulationDasboard
